/**
 * The sentences of the homework feedback page (#377, U1; #445), in English and Hebrew, keyed by the contract's own
 * enumerations and by nothing the server wrote. The notes' own wording (`note.text`, a rung's text) is the server's,
 * assembled from what each check covers (#406); nothing here adds to it.
 *
 * The rules the wording follows (`docs/feedback_research.md` §8):
 *
 * - **A label claims only what was checked** (principle 1). ✓ is "proved", ✗ is "refuted", ○ is "read, not checked" -
 *   never "correct", never "wrong" as a judgement of the learner.
 * - **No praise, no score, no ranking** (principle 6), and no count or proportion (`no-percentage.test.tsx`).
 * - **Every showing of help is recorded** (#406): each action that shows help states its cost before it is taken,
 *   derived from the rung's declared kind (#438).
 *
 * A message is a string or a function; record-shaped wording is reached through a function. A sentence that must carry
 * `<code>` or an untrusted-text element holds `{name}` placeholders, filled by `fill-template.tsx`.
 */

import { POST_ANSWER_LADDER } from '@davinci/domain';
import type { ErrorCode, HomeworkFeedbackResponse, HomeworkTriageResponse } from '@davinci/contracts';
import { defineMessages } from './messages.js';
import { useMessages, type UiLocale } from './locale.js';

type Note = HomeworkFeedbackResponse['notes'][number];
export type NoteMark = Note['mark'];
export type DeliveredRung = Note['rungs'][number]['rung'];
export type SolutionSource = NonNullable<Note['rungs'][number]['solutionSource']>;
export type PullableRung = Note['pullable'][number];
type QuoteReason = NonNullable<NonNullable<Note['location']>['reason']>;

/** The symbol for each mark. The symbol is decoration; the word (`markWord`) is what is read out. */
export const MARK_SYMBOLS: Readonly<Record<NoteMark, string>> = {
  proved: '✓',
  refuted: '✗',
  // #438 slice 3: the learner disputed a refuted line (no verdict is concluded yet), and the founder's overturning of one.
  under_review: '?',
  ruled_correct: '✓',
  read_not_checked: '○',
  refused: '○',
  undecided: '○',
  withheld: '○',
  on_request: '·',
};

type SubmissionRules = NonNullable<HomeworkFeedbackResponse['submissionRules']>;
type SubmissionRuleId = SubmissionRules['rules'][number]['ruleId'];
type SubmissionRuleMark = SubmissionRules['rules'][number]['mark'];
type SubmissionRuleReason = NonNullable<SubmissionRules['rules'][number]['reason']>;

const SUBMISSION_RULE_EN: Record<SubmissionRuleId, string> = {
  one_question_per_page: 'Each question on its own page',
  ascending_order: 'Questions in ascending order',
  upright_pages: 'No page upside down',
  title: 'A title with the course name and the exercise number',
  no_name_or_id: 'No name or ID on the pages',
};
const SUBMISSION_MARK_EN: Record<SubmissionRuleMark, string> = { met: 'Met', not_met: 'Not met', not_checked: 'Not checked' };
const SUBMISSION_REASON_EN: Record<SubmissionRuleReason, string> = {
  no_question_labels: 'no question labels were confirmed',
  labels_not_numbers: 'the question labels are not numbers',
  orientation_not_read: "DaVinci can't tell from the reading",
  page_unreadable: 'a page could not be read',
  no_display_name: 'no name is set to look for',
  exercise_number_unknown: "the sheet's exercise number is not known",
  course_unknown: 'the course is not known',
  lines_not_checked: 'some lines were not checked',
};
const SUBMISSION_RULE_HE: Record<SubmissionRuleId, string> = {
  one_question_per_page: 'כל שאלה בעמוד נפרד',
  ascending_order: 'השאלות לפי סדר עולה',
  upright_pages: 'אין עמוד הפוך',
  title: 'כותרת עם שם הקורס ומספר התרגיל',
  no_name_or_id: 'אין שם או מספר תעודת זהות על העמודים',
};
const SUBMISSION_MARK_HE: Record<SubmissionRuleMark, string> = { met: 'מתקיים', not_met: 'לא מתקיים', not_checked: 'לא נבדק' };
const SUBMISSION_REASON_HE: Record<SubmissionRuleReason, string> = {
  no_question_labels: 'לא אושרו תוויות שאלה',
  labels_not_numbers: 'תוויות השאלות אינן מספרים',
  orientation_not_read: 'DaVinci לא יכול לדעת זאת מהקריאה',
  page_unreadable: 'לא ניתן היה לקרוא עמוד',
  no_display_name: 'לא הוגדר שם לחיפוש',
  exercise_number_unknown: 'מספר התרגיל של הגיליון אינו ידוע',
  course_unknown: 'הקורס אינו ידוע',
  lines_not_checked: 'חלק מהשורות לא נבדקו',
};

const MARK_WORDS_EN: Record<NoteMark, string> = {
  proved: 'Proved',
  refuted: 'Refuted',
  under_review: 'Under review',
  ruled_correct: 'Reviewed',
  read_not_checked: 'Read, not checked',
  refused: 'Not checked: needs a course rule',
  undecided: 'Not decided',
  withheld: 'Not shown',
  on_request: 'Checked, closed',
};
const MARK_WORDS_HE: Record<NoteMark, string> = {
  proved: 'הוכח',
  refuted: 'נסתר',
  under_review: 'בבדיקה',
  ruled_correct: 'נבדק',
  read_not_checked: 'נקרא, לא נבדק',
  refused: 'לא נבדק: נדרש כלל מהקורס',
  undecided: 'לא הוכרע',
  withheld: 'לא מוצג',
  on_request: 'נבדק, סגור',
};


/** What a certificate kind establishes, in words (#361). An unknown kind is shown by its name alone. */
const CERTIFICATE_EN: Readonly<Record<string, string>> = {
  step_chain_proof: 'a proof that each step of the chain is an identity',
  step_chain_refutation: 'values at which one step of the chain does not hold',
  counterexample_valid_proof: 'a check that your values break the claim as you wrote it',
  counterexample_invalid: 'a check that your values do not break the claim as you wrote it',
  witness_proof: 'a check that your values satisfy every condition as you wrote it',
  witness_failure: 'a check that your values fail a condition as you wrote it',
  set_relation_proof: 'a check that the two sets have the same elements',
  set_relation_refutation: 'an element in one of the two sets and not the other',
};
const CERTIFICATE_HE: Readonly<Record<string, string>> = {
  step_chain_proof: 'הוכחה שכל צעד בשרשרת הוא זהות',
  step_chain_refutation: 'ערכים שבהם צעד אחד בשרשרת אינו מתקיים',
  counterexample_valid_proof: 'בדיקה שהערכים שלך סותרים את הטענה כפי שכתבת אותה',
  counterexample_invalid: 'בדיקה שהערכים שלך אינם סותרים את הטענה כפי שכתבת אותה',
  witness_proof: 'בדיקה שהערכים שלך מקיימים כל תנאי כפי שכתבת אותו',
  witness_failure: 'בדיקה שהערכים שלך אינם מקיימים תנאי כפי שכתבת אותו',
  set_relation_proof: 'בדיקה ששתי הקבוצות מכילות אותם איברים',
  set_relation_refutation: 'איבר שנמצא באחת משתי הקבוצות ולא בשנייה',
};

const RUNG_EN: Record<DeliveredRung, string> = {
  error_location: 'What and where',
  hint: 'Hint',
  counterexample: 'Counterexample',
  // DaVinci's own worked solution (#586); a founder-authored one delivered before it is titled plainly (see `rungWord`).
  solution: "DaVinci's solution",
};
const RUNG_HE: Record<DeliveredRung, string> = {
  error_location: 'מה ואיפה',
  hint: 'רמז',
  counterexample: 'דוגמה נגדית',
  solution: 'הפתרון של DaVinci',
};

const PULL_EN: Record<PullableRung, string> = {
  hint: 'Show a hint',
  counterexample: 'Show the counterexample',
  solution: 'Show the solution',
};
const PULL_HE: Record<PullableRung, string> = {
  hint: 'הצגת רמז',
  counterexample: 'הצגת הדוגמה הנגדית',
  solution: 'הצגת הפתרון',
};

/** "Tue 3 Nov 23:59": when the worked solution opens (#480), in the viewer's own time zone. */
function opensAt(intlLocale: string, iso: string): string {
  const at = new Date(iso);
  const parts = new Intl.DateTimeFormat(intlLocale, { weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(at);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  const time = new Intl.DateTimeFormat(intlLocale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
  return `${get('weekday')} ${get('day')} ${get('month')} ${time}`;
}

/** Said beside each action that shows help, before it is taken; the kind (D15), not the rung, picks the sentence. */
function rungCost(rung: PullableRung, informative: string, revealing: string): string {
  const kind = POST_ANSWER_LADDER[rung].kind;
  if (kind === 'informative') return informative;
  if (kind === 'revealing') return revealing;
  // A post-answer rung that cost nothing would manufacture independence; the domain forbids it, so fail loudly.
  throw new Error(`rung "${rung}" has kind "${kind}", which costs no independence; a post-answer rung must`);
}

/** Why a note quotes its line instead of marking it on the page (`note-location@1`). */
const QUOTE_REASON_EN: Record<QuoteReason, string> = {
  position_uncalibrated:
    "Not marked on the page: where the readers placed this page's lines could not be checked, so the line is quoted instead.",
  position_off_page: "Not marked on the page: the reader's position for this line is not on the page, so the line is quoted instead.",
  frame_covers_another_line: 'Not marked on the page: a frame here would also cover another line, so the line is quoted instead.',
};
const QUOTE_REASON_HE: Record<QuoteReason, string> = {
  position_uncalibrated:
    'לא מסומן על הדף: אי אפשר היה לוודא היכן הקוראים מיקמו את שורות הדף הזה, ולכן השורה מצוטטת.',
  position_off_page: 'לא מסומן על הדף: המיקום שהקורא נתן לשורה הזו אינו בתוך הדף, ולכן השורה מצוטטת.',
  frame_covers_another_line: 'לא מסומן על הדף: מסגרת כאן הייתה מכסה גם שורה אחרת, ולכן השורה מצוטטת.',
};

function pagesPhraseEn(pages: readonly number[]): string {
  if (pages.length === 1) return `page ${String(pages[0])}`;
  const head = pages.slice(0, -1).map(String).join(', ');
  return `pages ${head} and ${String(pages.at(-1))}`;
}
function pagesPhraseHe(pages: readonly number[]): string {
  if (pages.length === 1) return `עמוד ${String(pages[0])}`;
  const head = pages.slice(0, -1).map(String).join(', ');
  return `עמודים ${head} ו-${String(pages.at(-1))}`;
}

function failureEn(code: ErrorCode | undefined): string {
  switch (code) {
    case 'homework_not_checkable':
      // The reason is in the server's message, which the client does not carry: the sentence names what the learner can do.
      return 'This homework cannot be checked as it stands. Every page has to be read and confirmed, and the homework has to belong to an exercise whose course materials DaVinci can read. Nothing was recorded.';
    case 'check_scope_not_ready':
      return 'The check could not start right now. DaVinci keeps trying, so try again in a few minutes.';
    case 'rung_unavailable':
      return 'That help cannot be shown for this line now. Nothing was recorded.';
    case 'solution_unavailable':
      return 'DaVinci could not write a solution right now. Nothing was recorded. Try again later.';
    case 'dispute_not_refuted':
    case 'dispute_not_shown':
      return 'This line cannot be flagged now. Nothing was recorded.';
    case 'note_question_not_movable':
      return "This note can't be moved here. Its question was set on the page review.";
    case 'line_has_a_refutation':
      return 'DaVinci already found a problem on a line you skipped, so it stays in the check. You can correct it instead.';
    default:
      return '';
  }
}
function failureHe(code: ErrorCode | undefined): string {
  switch (code) {
    case 'homework_not_checkable':
      return 'אי אפשר לבדוק את שיעורי הבית כפי שהם. כל עמוד צריך להיקרא ולהיות מאושר, ושיעורי הבית צריכים להשתייך לתרגיל שאת חומרי הקורס שלו DaVinci יכולה לקרוא. דבר לא נרשם.';
    case 'check_scope_not_ready':
      return 'לא ניתן להתחיל את הבדיקה כרגע. DaVinci ממשיכה לנסות, אפשר לנסות שוב בעוד כמה דקות.';
    case 'rung_unavailable':
      return 'אי אפשר להציג עכשיו את העזרה הזו עבור השורה הזו. דבר לא נרשם.';
    case 'solution_unavailable':
      return 'DaVinci לא הצליחה לכתוב עכשיו פתרון. דבר לא נרשם. נסו שוב מאוחר יותר.';
    case 'dispute_not_refuted':
    case 'dispute_not_shown':
      return 'אי אפשר לסמן עכשיו את השורה הזו. דבר לא נרשם.';
    case 'note_question_not_movable':
      return 'אי אפשר להעביר את ההערה הזו מכאן. השאלה שלה נקבעה בבדיקת העמודים.';
    case 'line_has_a_refutation':
      return 'DaVinci כבר מצאה בעיה בשורה שדולגה, ולכן היא נשארת בבדיקה. אפשר לתקן אותה במקום זאת.';
    default:
      return '';
  }
}

export type TriageClass = HomeworkTriageResponse['questions'][number]['triageClass'];

const TRIAGE_EN: Readonly<Record<TriageClass, string>> = {
  can_verify: 'DaVinci can check this',
  feedback_only: 'Feedback only — not checked',
  cant_read: "Couldn't read — please fix the transcription",
};
const TRIAGE_HE: Readonly<Record<TriageClass, string>> = {
  can_verify: 'DaVinci יכול לבדוק זאת',
  feedback_only: 'משוב בלבד — לא נבדק',
  cant_read: 'לא ניתן לקרוא — אנא תקנו את התעתיק',
};

const CHANNEL_FAILED_EN: Readonly<Record<string, string>> = {
  consent_refused: 'Feedback is off because you have not agreed to sending your work for feedback. You can change that in your consent settings.',
  budget_refused: "Feedback is off because this period's allowance for model help is used up.",
  unavailable: 'Feedback could not be produced right now.',
  invalid_output: 'Feedback could not be produced: the reply could not be used.',
  payload_refused: 'Feedback could not be produced: this work could not be sent.',
  no_question_sheet: "Feedback could not be produced: the question sheet for this exercise isn't available.",
};
const CHANNEL_FAILED_HE: Readonly<Record<string, string>> = {
  consent_refused: 'המשוב כבוי כי לא הסכמתם לשלוח את העבודה לצורך משוב. אפשר לשנות זאת בהגדרות ההסכמה.',
  budget_refused: 'המשוב כבוי כי המכסה לעזרת מודל בתקופה זו נוצלה.',
  unavailable: 'לא ניתן היה להפיק משוב כרגע.',
  invalid_output: 'לא ניתן היה להפיק משוב: לא ניתן היה להשתמש בתשובה.',
  payload_refused: 'לא ניתן היה להפיק משוב: לא ניתן היה לשלוח את העבודה.',
  no_question_sheet: 'לא ניתן היה להפיק משוב: דף השאלות של התרגיל אינו זמין.',
};

/** Failures where trying again cannot help until the learner changes something else. */
export const CHANNEL_NO_RETRY: ReadonlySet<string> = new Set(['consent_refused', 'budget_refused']);

export const feedbackLabels = defineMessages({
  en: {
    triageHeading: 'Before the check',
    triageIntro: 'How DaVinci will handle each question. Starting the check is still up to you.',
    triageNoQuestion: 'Lines with no question number',
    triageClassWord: (c: TriageClass): string => TRIAGE_EN[c],
    channelHeading: 'Feedback (not a grade)',
    channelIntro: 'A second reader may point at lines to look at again. This is not a check and not a grade.',
    channelPending: 'Looking at your work for lines to check again…',
    channelPageHeading: (n: number): string => `Page ${String(n)}`,
    channelFlagDefault: 'Check this line',
    channelFlagName: (n: number): string => `Flag ${String(n)}`,
    channelWhyAction: 'Why?',
    channelWhyCost: 'Opening this counts as help on this question.',
    channelReasonLoading: 'Opening the reason…',
    channelReasonFailed: 'The reason could not be opened. Try again.',
    channelOpenFailed: 'The lines to check could not be shown. Try again.',
    channelDrift: "Feedback is paused: the feedback model changed and hasn't been re-checked yet.",
    channelFailed: (kind: string | null): string => CHANNEL_FAILED_EN[kind ?? ''] ?? (CHANNEL_FAILED_EN['unavailable'] as string),
    channelRetryAction: 'Try again',
    channelGetAction: 'Get feedback',
    channelNothing: 'No feedback was requested for this work.',
    markWord: (mark: NoteMark): string => MARK_WORDS_EN[mark],
    certificateWord: (kind: string): string => CERTIFICATE_EN[kind] ?? kind,
    rungWord: (rung: DeliveredRung, source?: SolutionSource): string => (rung === 'solution' && source === 'authored' ? 'Solution' : RUNG_EN[rung]),
    /** Said beside DaVinci's own worked solution (#586): it is not the course's answer and may contain mistakes. */
    solutionNotOfficial: 'Not official · can be wrong',
    pullAction: (rung: PullableRung): string => PULL_EN[rung],
    /** The worked solution's control while it is closed (#480): disabled, naming when it opens. */
    solutionOpens: (iso: string): string => `${PULL_EN.solution} · opens ${opensAt('en-GB', iso)}`,
    /** The canvas's few words beside a rung (#673); the full sentence above stays as the button's description. */
    rungCostShort: (rung: PullableRung): string => rungCost(rung, 'counts as help', 'shows the answer, counts as help'),
    rungCost: (rung: PullableRung): string =>
      rungCost(
        rung,
        'Recorded as help you saw: a fix of this line after it counts as done with help.',
        'Recorded as help that shows the answer: a fix of this line after it counts as done with help.',
      ),
    quoteReason: (reason: QuoteReason): string => QUOTE_REASON_EN[reason],
    frameWords: 'The dashed frame marks the area, not an exact position.',
    certificateSummary: 'How this was checked',
    learnerTyped: 'you typed this',
    /** `{words}`, `{kind}`, `{check}` and `{kernel}`; the last three are shown as code. */
    certificateLine: 'Certificate: {words} ({kind}, check {check}, {kernel}).',
    ruleUsedIntro: 'Decided under this course rule:',
    ruleUnquoted: (ruleId: string): string =>
      `Decided under the course rule ${ruleId}. The course materials have changed since this check, so its wording is not quoted here; check again to see it.`,
    ruleNotSourced: (ruleId: string): string =>
      `This check needs the course rule ${ruleId}, which has not yet been quoted from the course materials. Until it is, this line is not checked.`,
    rulePage: (page: string): string => `page ${page}`,
    checkAction: 'Check my homework',
    openFeedbackExplanation:
      'Opening the feedback shows what was checked on every line. Where a line does not follow, it shows what and where, and that is recorded as help you saw.',
    notCheckedYet: 'This homework has not been checked yet.',
    /** Group heading for notes whose first line carries no question label. */
    noQuestionGroup: 'Lines not marked with a question',
    /** What a refused check or rung says, keyed by the server's error code; undefined falls back to the generic notice. */
    failureSentence: failureEn,

    // --- Confidence before the verdict (#438 slice 2, HYP-UX-005)
    /** `{subject}` is the question label (untrusted text). */
    confidencePromptLabel: 'Before you look: how sure are you of {subject}?',
    /** The one prompt an upload with no labelled lines gets. */
    confidencePromptSheet: 'Before you look: how sure are you of this homework?',
    confidenceSure: 'Sure',
    confidenceNotSure: 'Unsure',
    confidenceGuessed: 'Guessed',
    confidenceSkippable:
      'Optional. You can leave any of these blank. Your answers are kept as you give them and are not a score.',

    // --- Disputing a verdict (#438 slice 3)
    underReviewText: 'Under review: you said this verdict looks wrong. It will be reviewed by the date shown.',
    disputeAction: 'This verdict looks wrong',
    disputeConfirmQuestion: 'Flag this for review? It will be looked at before the due date.',
    disputeConfirmAction: 'Flag it',
    disputeCancelAction: 'Cancel',
    underReviewChip: 'Under review',
    /** A question of a resubmission that an earlier upload had wrong and this one proves (#415): after feedback, so never read as independent. */
    fixedChip: 'Fixed · תוקן',
    fixedAfterFeedback: (formattedDate: string): string => `Fixed after feedback (the mistake was on the upload of ${formattedDate}).`,
    // --- Submission rules (#415): the course's format rules for handing in work, never about what the learner knows
    submissionRulesHeading: 'Submission rules',
    submissionRulesIntro: 'The course rules for handing in a sheet. This is about the format, not about what you know.',
    submissionRulesPolicy: 'Decided by {policy}, from the question labels and text you confirmed.',
    submissionRuleText: (ruleId: SubmissionRuleId): string => SUBMISSION_RULE_EN[ruleId],
    submissionRuleMark: (mark: SubmissionRuleMark): string => SUBMISSION_MARK_EN[mark],
    submissionRuleReason: (reason: SubmissionRuleReason): string => SUBMISSION_REASON_EN[reason],
    ruleBy: (formattedDate: string): string => `Ruling by ${formattedDate}`,

    // --- The weak-point record (#438 / #439)
    /** Said of a refuted line only when the server says it is in the record (`examPrepNoted`); the client never derives it. */
    examPrepNoted: 'Noted for your exam prep.',
    /** The summary topic line when the Moodle section has no name. */
    topicLineFallback: (sectionNumber: string): string => `Section ${sectionNumber}`,

    // --- The page
    pagesPhrase: pagesPhraseEn,
    pageImageLabel: (pageNumber: number): string =>
      `Page ${String(pageNumber)}, with each note's area marked by a dashed frame and its number`,
    feedbackPageHeading: 'Feedback on your homework',
    backToPages: 'Back to the pages',
    loadingFeedback: 'Loading the feedback…',
    checkingHomework: 'Checking your homework…',
    summaryHeading: 'Summary',
    provedOn: 'Proved, on {pages}: {text}',
    refutedOn: 'Refuted, on {pages}: {text}',
    notCheckedOn: (pages: string, needsCourseRule: boolean): string =>
      `Some lines on ${pages} were read and not checked${needsCourseRule ? ', some because a check needs a course rule not yet quoted from the course materials' : ''}.`,
    /** Lines the check read and left out (#646): said whenever there are any. A count of lines, never of what the learner knows. */
    notCheckedLines: (count: number): string => `DaVinci did not check ${count === 1 ? '1 line' : `${String(count)} lines`} of this homework.`,
    /** Said beside it when no claim was decided at all. */
    nothingCheckedYet: 'Nothing on this homework could be checked yet.',
    byQuestionHeading: 'By question',
    columnQuestion: 'Question',
    columnNote: 'Note',
    columnPage: 'Page',
    columnFound: 'Found',
    howCheckedSummary: 'How this was checked',
    /** `{at}` plain; `{policy}`, `{kernel}`, `{context}`, `{digest}`, `{questions}` and `{location}` are shown as code. */
    provenance:
      'Checked {at} under {policy}, with the certificate kernel {kernel}, course context {context} ({digest}) and note positions {location}.',
    provenanceWithQuestions:
      'Checked {at} under {policy}, with the certificate kernel {kernel}, course context {context} ({digest}), questions {questions} and note positions {location}.',
  },
  he: {
    triageHeading: 'לפני הבדיקה',
    triageIntro: 'כך DaVinci יטפל בכל שאלה. התחלת הבדיקה נשארת בידיים שלך.',
    triageNoQuestion: 'שורות בלי מספר שאלה',
    triageClassWord: (c: TriageClass): string => TRIAGE_HE[c],
    channelHeading: 'משוב (לא ציון)',
    channelIntro: 'קורא שני עשוי להצביע על שורות שכדאי לבדוק שוב. זו אינה בדיקה ואינה ציון.',
    channelPending: 'מעיינים בעבודה שלך לחפש שורות לבדוק שוב…',
    channelPageHeading: (n: number): string => `עמוד ${String(n)}`,
    channelFlagDefault: 'כדאי לבדוק שורה זו',
    channelFlagName: (n: number): string => `סימון ${String(n)}`,
    channelWhyAction: 'למה?',
    channelWhyCost: 'פתיחת ההסבר נחשבת עזרה בשאלה זו.',
    channelReasonLoading: 'פותחים את ההסבר…',
    channelReasonFailed: 'לא ניתן היה לפתוח את ההסבר. נסו שוב.',
    channelOpenFailed: 'לא ניתן היה להציג את השורות לבדיקה. נסו שוב.',
    channelDrift: 'המשוב מושהה: מודל המשוב השתנה ועדיין לא נבדק מחדש.',
    channelFailed: (kind: string | null): string => CHANNEL_FAILED_HE[kind ?? ''] ?? (CHANNEL_FAILED_HE['unavailable'] as string),
    channelRetryAction: 'נסו שוב',
    channelGetAction: 'קבלו משוב',
    channelNothing: 'לא התבקש משוב לעבודה זו.',
    markWord: (mark: NoteMark): string => MARK_WORDS_HE[mark],
    certificateWord: (kind: string): string => CERTIFICATE_HE[kind] ?? kind,
    rungWord: (rung: DeliveredRung, source?: SolutionSource): string => (rung === 'solution' && source === 'authored' ? 'פתרון' : RUNG_HE[rung]),
    solutionNotOfficial: 'לא רשמי · ייתכנו בו טעויות',
    pullAction: (rung: PullableRung): string => PULL_HE[rung],
    solutionOpens: (iso: string): string => `${PULL_HE.solution} · נפתח ב${opensAt('he-IL', iso)}`,
    rungCostShort: (rung: PullableRung): string => rungCost(rung, 'נספר כעזרה', 'מראה את התשובה, נספר כעזרה'),
    rungCost: (rung: PullableRung): string =>
      rungCost(
        rung,
        'נרשם כעזרה שראית: תיקון של השורה הזו אחריה נחשב כנעשה בעזרה.',
        'נרשם כעזרה שמראה את התשובה: תיקון של השורה הזו אחריה נחשב כנעשה בעזרה.',
      ),
    quoteReason: (reason: QuoteReason): string => QUOTE_REASON_HE[reason],
    frameWords: 'המסגרת המקווקוות מסמנת את האזור, לא מיקום מדויק.',
    certificateSummary: 'איך זה נבדק',
    learnerTyped: 'הקלדת את זה',
    certificateLine: 'תעודת אימות: {words} ({kind}, בדיקה {check}, {kernel}).',
    ruleUsedIntro: 'נקבע לפי כלל הקורס הזה:',
    ruleUnquoted: (ruleId: string): string =>
      `נקבע לפי כלל הקורס ${ruleId}. חומרי הקורס השתנו מאז הבדיקה הזו, ולכן הנוסח שלו אינו מצוטט כאן. כדאי לבדוק שוב כדי לראות אותו.`,
    ruleNotSourced: (ruleId: string): string =>
      `הבדיקה הזו מצריכה את כלל הקורס ${ruleId}, שעדיין לא צוטט מחומרי הקורס. עד שיצוטט, השורה הזו אינה נבדקת.`,
    rulePage: (page: string): string => `עמוד ${page}`,
    checkAction: 'בדיקת שיעורי הבית שלי',
    openFeedbackExplanation:
      'פתיחת המשוב מציגה מה נבדק בכל שורה. כשצעד בשורה אינו נובע, היא מציגה מה ואיפה, וזה נרשם כעזרה שראית.',
    notCheckedYet: 'שיעורי הבית האלה עדיין לא נבדקו.',
    noQuestionGroup: 'שורות שלא סומנו בשאלה',
    failureSentence: failureHe,

    confidencePromptLabel: 'לפני שמסתכלים: מה רמת הביטחון שלך בתשובה לשאלה {subject}?',
    confidencePromptSheet: 'לפני שמסתכלים: מה רמת הביטחון שלך בשיעורי הבית האלה?',
    confidenceSure: 'ביטחון מלא',
    confidenceNotSure: 'עם ספקות',
    confidenceGuessed: 'ניחוש',
    confidenceSkippable:
      'רשות. אפשר להשאיר כל אחד מאלה ריק. התשובות שלך נשמרות כפי שנתת אותן, והן אינן ציון.',

    underReviewText: 'בבדיקה: ציינת שההכרעה הזו נראית שגויה. היא תיבדק עד התאריך המוצג.',
    disputeAction: 'נראה שהפסק שגוי',
    disputeConfirmQuestion: 'לסמן את זה לבדיקה? יעיינו בזה לפני מועד ההגשה.',
    disputeConfirmAction: 'סימון לבדיקה',
    disputeCancelAction: 'ביטול',
    underReviewChip: 'בבדיקה',
    fixedChip: 'תוקן · Fixed',
    fixedAfterFeedback: (formattedDate: string): string => `תוקן אחרי משוב (הטעות הייתה בהעלאה מ-${formattedDate}).`,
    // --- כללי ההגשה (#415): כללי הקורס לפורמט ההגשה, ולא על מה שהלומד יודע
    submissionRulesHeading: 'כללי הגשה',
    submissionRulesIntro: 'כללי הקורס להגשת גיליון. זה נוגע לפורמט ההגשה, לא לידע שלך.',
    submissionRulesPolicy: 'נקבע לפי {policy}, מתוויות השאלות והטקסט שאישרת.',
    submissionRuleText: (ruleId: SubmissionRuleId): string => SUBMISSION_RULE_HE[ruleId],
    submissionRuleMark: (mark: SubmissionRuleMark): string => SUBMISSION_MARK_HE[mark],
    submissionRuleReason: (reason: SubmissionRuleReason): string => SUBMISSION_REASON_HE[reason],
    ruleBy: (formattedDate: string): string => `הכרעה עד ${formattedDate}`,

    examPrepNoted: 'נרשם להכנה למבחן שלך.',
    topicLineFallback: (sectionNumber: string): string => `יחידה ${sectionNumber}`,

    pagesPhrase: pagesPhraseHe,
    pageImageLabel: (pageNumber: number): string =>
      `עמוד ${String(pageNumber)}, כשאזור כל הערה מסומן במסגרת מקווקוות ובמספרה`,
    feedbackPageHeading: 'משוב על שיעורי הבית שלך',
    backToPages: 'חזרה לעמודים',
    loadingFeedback: 'טוענים את המשוב…',
    checkingHomework: 'בודקים את שיעורי הבית שלך…',
    summaryHeading: 'סיכום',
    provedOn: 'הוכח, ב{pages}: {text}',
    refutedOn: 'נסתר, ב{pages}: {text}',
    notCheckedOn: (pages: string, needsCourseRule: boolean): string =>
      `חלק מהשורות ב${pages} נקראו ולא נבדקו${needsCourseRule ? ', חלקן מפני שבדיקה מצריכה כלל קורס שעדיין לא צוטט מחומרי הקורס' : ''}.`,
    notCheckedLines: (count: number): string => `DaVinci לא בדקה ${count === 1 ? 'שורה אחת' : `${String(count)} שורות`} בשיעורי הבית האלה.`,
    nothingCheckedYet: 'עדיין לא ניתן היה לבדוק שום דבר בשיעורי הבית האלה.',
    byQuestionHeading: 'לפי שאלה',
    columnQuestion: 'שאלה',
    columnNote: 'הערה',
    columnPage: 'עמוד',
    columnFound: 'ממצא',
    howCheckedSummary: 'איך זה נבדק',
    provenance:
      'נבדק ב-{at} לפי {policy}, עם גרעין האימות {kernel}, הקשר הקורס {context} ({digest}) ומיקומי ההערות {location}.',
    provenanceWithQuestions:
      'נבדק ב-{at} לפי {policy}, עם גרעין האימות {kernel}, הקשר הקורס {context} ({digest}), שאלות {questions} ומיקומי ההערות {location}.',
  },
});

export type FeedbackLabels = (typeof feedbackLabels)['en'];

/** The feedback wording in the interface language. */
export function useFeedbackLabels(): FeedbackLabels {
  return useMessages(feedbackLabels);
}

/** For code outside React: the wording in one language. */
export function feedbackLabelsFor(locale: UiLocale): FeedbackLabels {
  return feedbackLabels[locale];
}

/** English-by-default form of `rungCost`, for callers with no locale. */
export function rungCostSentence(rung: PullableRung, locale: UiLocale = 'en'): string {
  return feedbackLabels[locale].rungCost(rung);
}
